import { ErrorMessage, Field } from "formik";
import { useState } from "react";
import { formatUnderscoreToSpace } from "../../utils/text-format";
import deleteIcon from "../../assets/icons/delete-bin.svg";

const FormTextArrayField = ({
  name = "textArrayField",
  label = "textArrayField",
  labelClassName = "",
  placeholder = "Enter value...",
  className = "",
  itemClassName = "",
  required = false,
}) => {
  const [newValue, setNewValue] = useState("");

  return (
    <div className={`w-full ${className}`}>
      <Field name={name}>
        {({ form, meta }) => {
          const items = form.values[name] || [];
          const hasError = meta.touched && meta.error;
          const isDuplicate = items.includes(newValue.trim());

          const handleAdd = () => {
            const trimmed = newValue.trim();
            if (trimmed && !isDuplicate) {
              form.setFieldValue(name, [...items, trimmed]);
              form.setFieldTouched(name, true, false);
              setNewValue("");
            }
          };

          const handleKeyDown = (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          };

          const handleRemove = (index) => {
            const updated = items.filter((_, i) => i !== index);
            form.setFieldValue(name, updated);
            form.setFieldTouched(name, true, false);
          };

          return (
            <div className="flex flex-col gap-2">
              {/* Header Label */}
              {label && (
                <label
                  htmlFor={`input-${name}`}
                  className={`text-fs_14 md:text-fs_16 font-medium capitalize select-none transition-colors ${
                    hasError
                      ? "text-errorColor"
                      : labelClassName || "text-darkText"
                  }`}
                >
                  {formatUnderscoreToSpace(label)}
                  {required && (
                    <span className="text-errorColor ml-0.5">*</span>
                  )}
                </label>
              )}

              {/* Input Group */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <input
                    id={`input-${name}`}
                    type="text"
                    value={newValue}
                    onKeyDown={handleKeyDown}
                    onChange={(e) => setNewValue(e.target.value)}
                    placeholder={placeholder}
                    className={`px-3 py-2.5 w-full rounded-lg text-fs_14 md:text-fs_16
                      text-darkText placeholder-greyText placeholder:text-fs_14 md:placeholder:text-fs_16
                      bg-white border transition-all duration-200 focus:outline-none ${
                        hasError
                          ? "border-errorColor focus:ring-2 focus:ring-errorColor/20"
                          : "border-greyText hover:border-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/20"
                      }`}
                  />
                  {isDuplicate && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-errorColor text-xs font-medium">
                      Already exists
                    </span>
                  )}
                </div>

                <button
                  type="button"
                  disabled={!newValue.trim() || isDuplicate}
                  onClick={handleAdd}
                  className="px-4 py-2.5 bg-primary text-white text-fs_14 rounded-lg font-medium 
                    hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-95 whitespace-nowrap"
                >
                  Add
                </button>
              </div>

              {/* Tag / Chip Display List */}
              {items.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {items.map((item, index) => (
                    <span
                      key={`${item}-${index}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-fs_14 text-darkText transition-all hover:bg-slate-200/70 ${itemClassName}`}
                    >
                      <span className="truncate max-w-[200px]">{item}</span>
                      <button
                        type="button"
                        onClick={() => handleRemove(index)}
                        className="p-1 bg-red-500 rounded-full transition-colors flex items-center justify-center"
                        aria-label={`Remove ${item}`}
                      >
                        <img
                          src={deleteIcon}
                          alt="Remove item"
                          className="w-3.5 h-3.5 opacity-70 hover:opacity-100"
                        />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {/* Error Message Container */}
              <div className="text-errorColor text-fs_14 ps-1 min-h-[1.25rem] flex items-center">
                <ErrorMessage name={name} />
              </div>
            </div>
          );
        }}
      </Field>
    </div>
  );
};

export default FormTextArrayField;
